/**
 * Destructive-confirmation dialog: round icon, title, body, confirm, cancel.
 *
 * This is the shape the "Delete this song?" dialog already had; the delete-account
 * dialog is the same shape with extra fields, so it composes this rather than
 * duplicating it. Styles verbatim, including the `#2A0A05` confirm text colour,
 * which is a one-off literal in the source rather than a token.
 */
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button, Modal } from '../ui';
import { c } from '../../styles/theme';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Body copy, or arbitrary content for dialogs with extra fields. */
  children: ReactNode;
  /** Round badge icon above the title, e.g. <Trash2 size={19} color={c.danger} />. */
  icon?: ReactNode;
  confirmLabel: string;
  /** Leading icon inside the confirm button. */
  confirmIcon?: ReactNode;
  onConfirm: () => void;
  confirmDisabled?: boolean;
  cancelLabel?: string;
  /** Renders the confirm button in the danger colour. */
  destructive?: boolean;
  /** Adds a close X beside the icon. Matches the delete-account dialog. */
  showClose?: boolean;
}

export default function ConfirmDialog({
  open,
  onClose,
  title,
  children,
  icon,
  confirmLabel,
  confirmIcon,
  onConfirm,
  confirmDisabled,
  cancelLabel = 'Cancel',
  destructive,
  showClose,
}: ConfirmDialogProps) {
  const badge = icon && (
    <div
      style={{
        width: 42,
        height: 42,
        borderRadius: '50%',
        background: destructive ? c.dangerSoft : c.accentSoft,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: showClose ? 0 : 12,
      }}
    >
      {icon}
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} ariaLabel={title}>
      {showClose ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          {badge}
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            aria-label="Close"
          >
            <X size={16} color={c.text2} />
          </button>
        </div>
      ) : (
        badge
      )}

      <p style={{ fontWeight: 600, fontSize: 15, margin: '0 0 4px', fontFamily: 'Space Grotesk' }}>
        {title}
      </p>
      {children}

      <Button
        full
        onClick={onConfirm}
        disabled={confirmDisabled}
        style={{
          marginBottom: 8,
          ...(destructive ? { background: c.dangerFill, color: '#2A0A05' } : null),
        }}
      >
        {confirmIcon}
        {confirmLabel}
      </Button>
      <Button full variant="ghost" onClick={onClose}>
        {cancelLabel}
      </Button>
    </Modal>
  );
}
